// src/pages/Documents.js
// Practice-wide document manager — browse, download, delete uploaded files

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts';
import * as awsApi from '../services/awsApiService';
import * as apiService from '../services/apiService';
import {
  FileText, Download, Trash2, Search, RefreshCw,
  AlertCircle, ScanLine, File, Image, X, ChevronLeft, ChevronRight
} from 'lucide-react';

const PAGE_SIZE = 25;

const TYPE_COLORS = {
  'Insurance Card': { bg: 'bg-blue-100', text: 'text-blue-700' },
  'EOB':            { bg: 'bg-green-100', text: 'text-green-700' },
  'X-Ray':          { bg: 'bg-purple-100', text: 'text-purple-700' },
  'Treatment Plan': { bg: 'bg-orange-100', text: 'text-orange-700' },
  'Referral':       { bg: 'bg-teal-100', text: 'text-teal-700' },
  'Clinical Notes': { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  'Other':          { bg: 'bg-gray-100', text: 'text-gray-600' },
};

function formatBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(d) {
  return d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

function TypeBadge({ type }) {
  const c = TYPE_COLORS[type] || TYPE_COLORS['Other'];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      {type || 'Other'}
    </span>
  );
}

export default function Documents() {
  const { practice } = useAuth();
  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const res = await awsApi.listDocuments({ limit: PAGE_SIZE, offset: page * PAGE_SIZE });
    if (res.success) {
      setDocs(res.documents);
      setTotal(res.total);
    }
    setLoading(false);
  }, [page]);

  useEffect(() => {
    if (apiService.getAppMode() === 'demo') {
      setDocs([
        { id: '1', document_type: 'Insurance Card', file_name: 'delta_dental_card.jpg', file_size: 84200, mime_type: 'image/jpeg', patient_name: 'Sarah Mitchell', claim_number: null, uploaded_by: 'admin@demo.com', created_at: '2026-05-20T14:30:00Z' },
        { id: '2', document_type: 'EOB',            file_name: 'eob_delta_2026.pdf',    file_size: 142000, mime_type: 'application/pdf', patient_name: 'Robert Johnson', claim_number: 'CLM-2026-0318', uploaded_by: 'admin@demo.com', created_at: '2026-05-18T09:15:00Z' },
        { id: '3', document_type: 'X-Ray',          file_name: 'tooth19_periapical.jpg', file_size: 320000, mime_type: 'image/jpeg', patient_name: 'Maria Garcia', claim_number: 'CLM-2026-0205', uploaded_by: 'admin@demo.com', created_at: '2026-05-15T11:00:00Z' },
        { id: '4', document_type: 'Treatment Plan', file_name: 'treatment_plan_q2.pdf', file_size: 98000, mime_type: 'application/pdf', patient_name: 'Emily Chen', claim_number: null, uploaded_by: 'admin@demo.com', created_at: '2026-05-10T16:45:00Z' },
      ]);
      setTotal(4);
      setLoading(false);
      return;
    }
    load();
  }, [load, page]);

  const filtered = search
    ? docs.filter(d =>
        d.file_name?.toLowerCase().includes(search.toLowerCase()) ||
        d.patient_name?.toLowerCase().includes(search.toLowerCase()) ||
        d.document_type?.toLowerCase().includes(search.toLowerCase()) ||
        d.claim_number?.toLowerCase().includes(search.toLowerCase())
      )
    : docs;

  const handleDownload = async (doc) => {
    if (apiService.getAppMode() === 'demo') { showToast('Download available in production mode'); return; }
    setDownloading(doc.id);
    const res = await awsApi.getDocumentUrl(doc.id);
    if (res.success && res.url) {
      window.open(res.url, '_blank');
    } else {
      showToast('Could not generate download link', 'error');
    }
    setDownloading(null);
  };

  const handleDelete = async (doc) => {
    setDeleting(doc.id);
    setConfirmDelete(null);
    const res = await awsApi.deleteDocument(doc.id);
    if (res.success) {
      setDocs(prev => prev.filter(d => d.id !== doc.id));
      setTotal(prev => prev - 1);
      showToast('Document deleted');
    } else {
      showToast(res.error || 'Delete failed', 'error');
    }
    setDeleting(null);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          padding: '12px 18px', borderRadius: 10, fontWeight: 600, fontSize: 14,
          background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
          border: `1px solid ${toast.type === 'error' ? '#fecaca' : '#bbf7d0'}`,
          color: toast.type === 'error' ? '#b91c1c' : '#15803d',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 }}>Documents</h1>
        <p style={{ color: '#64748b', fontSize: 14, marginTop: 4 }}>
          All uploaded files — EOBs, insurance cards, X-rays, and more. {total > 0 && `${total} total.`}
        </p>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 360 }}>
          <Search style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 15, height: 15, color: '#94a3b8' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by file name, patient, type..."
            style={{ width: '100%', padding: '8px 12px 8px 32px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              <X style={{ width: 14, height: 14, color: '#94a3b8' }} />
            </button>
          )}
        </div>
        {apiService.getAppMode() !== 'demo' && (
          <button
            onClick={load}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', fontSize: 13, cursor: 'pointer', color: '#374151' }}
          >
            <RefreshCw style={{ width: 14, height: 14 }} /> Refresh
          </button>
        )}
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading documents...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <ScanLine style={{ width: 40, height: 40, color: '#cbd5e1', margin: '0 auto 12px' }} />
            <p style={{ color: '#64748b', fontWeight: 600 }}>{search ? 'No documents match your search' : 'No documents yet'}</p>
            <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>Documents are saved automatically when you run OCR Document Capture in AI Tools.</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                {['File', 'Type', 'Patient', 'Claim', 'Size', 'Uploaded', ''].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(doc => (
                <tr key={doc.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {doc.mime_type?.startsWith('image/') ? (
                        <Image style={{ width: 16, height: 16, color: '#8b5cf6', flexShrink: 0 }} />
                      ) : (
                        <File style={{ width: 16, height: 16, color: '#3b82f6', flexShrink: 0 }} />
                      )}
                      <span style={{ fontSize: 13, fontWeight: 500, color: '#0f172a', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {doc.file_name}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px' }}><TypeBadge type={doc.document_type} /></td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151' }}>{doc.patient_name || '—'}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b', fontFamily: 'monospace' }}>{doc.claim_number || '—'}</td>
                  <td style={{ padding: '12px 16px', fontSize: 12, color: '#64748b' }}>{formatBytes(doc.file_size)}</td>
                  <td style={{ padding: '12px 16px', fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{formatDate(doc.created_at)}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => handleDownload(doc)}
                        disabled={downloading === doc.id}
                        title="Download"
                        style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 7, background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#374151' }}
                      >
                        <Download style={{ width: 13, height: 13 }} />
                        {downloading === doc.id ? '...' : 'Download'}
                      </button>
                      {confirmDelete?.id === doc.id ? (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button
                            onClick={() => handleDelete(doc)}
                            disabled={deleting === doc.id}
                            style={{ padding: '6px 10px', border: 'none', borderRadius: 7, background: '#ef4444', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                          >
                            {deleting === doc.id ? '...' : 'Confirm'}
                          </button>
                          <button
                            onClick={() => setConfirmDelete(null)}
                            style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 7, background: '#fff', cursor: 'pointer', fontSize: 12, color: '#374151' }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDelete(doc)}
                          title="Delete"
                          style={{ padding: '6px 8px', border: '1px solid #fecaca', borderRadius: 7, background: '#fef2f2', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        >
                          <Trash2 style={{ width: 13, height: 13, color: '#ef4444' }} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: page === 0 ? 'default' : 'pointer', opacity: page === 0 ? 0.4 : 1, display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}
          >
            <ChevronLeft style={{ width: 14, height: 14 }} /> Prev
          </button>
          <span style={{ fontSize: 13, color: '#64748b' }}>Page {page + 1} of {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: page >= totalPages - 1 ? 'default' : 'pointer', opacity: page >= totalPages - 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}
          >
            Next <ChevronRight style={{ width: 14, height: 14 }} />
          </button>
        </div>
      )}
    </div>
  );
}
